import { ResourceFilterType, useListResources } from '@client-app-sdk';
import { Combobox, ComboboxOption } from '@common/ui/Combobox/Combobox';
import { ResourceClientModel } from '@client-app-sdk/api/v1';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

type ResourceComboboxProps = {
  integrationId: string;
  resourceTypeId: string;
  value: ComboboxOption[] | null;
  onChange: (value: ComboboxOption[] | null) => void;
  sessionId?: string;
};

export function ResourceCombobox({ integrationId, resourceTypeId, onChange, value, sessionId }: ResourceComboboxProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { data, isFetching, hasNextPage, fetchNextPage } = useListResources({
    integrationId,
    resourceTypeId,
    limit: 100,
    search: debouncedSearch,
    sessionId,
  });

  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const resourcesComboboxOptions = useMemo(() => {
    const options: ComboboxOption[] =
      data?.pages.flatMap((page: { data: ResourceClientModel[] }) =>
        page.data.map((resource) => ({
          value: `${ResourceFilterType.Id}:${resource.id}`,
          label: resource.path,
          icon: resource.type.icons.svg,
        })),
      ) ?? [];

    const searchTotal = data?.pages[0].pagination.total;
    if (!!debouncedSearch && searchTotal) {
      options.unshift({
        value: `${ResourceFilterType.SearchTerm}:${debouncedSearch}`,
        label: `all ${searchTotal} matching "${debouncedSearch}"`,
      });
    }

    return options;
  }, [data, debouncedSearch]);

  return (
    <Combobox
      multiple
      value={value}
      label="Resources"
      inputValue={search}
      loading={isFetching}
      fetchMore={fetchMore}
      onInputChange={setSearch}
      disabled={!resourceTypeId}
      placeholder="Select resources"
      options={resourcesComboboxOptions}
      onChange={(val) => onChange(Array.isArray(val) ? val : null)}
    />
  );
}
